import { useMemo, useState } from 'react';
import type { MsrMbus } from './typen';
import {
  ContentSection, DataTable, EmptyState, FilterBar, KpiCard, KpiReihe, StatusBadge, formatiereZahl,
  type FilterDefinition, type FilterWerte, type Spalte,
} from '../../muster';

type Segment = MsrMbus['segmente'][number];
type Zaehler = NonNullable<MsrMbus['zaehler']>[number];

const MEDIUM_TEXT: Record<string, string> = {
  waerme: 'Wärme', kaelte: 'Kälte', wasser: 'Wasser', strom: 'Strom',
};

/**
 * Reiter „M-Bus" — der Adressplan der Zähler (Festlegung des Fachplaners vom
 * 16.09.2026, Frage 64: „M-Bus; eigener Reiter M-Bus").
 *
 * --- Warum Segment und Primäradresse und nicht nur eine Liste --------------
 *
 * Die Primäradresse wird am Zähler eingestellt — von Hand, auf der Baustelle,
 * nach dieser Liste. Zwei Zähler mit derselben Adresse auf derselben Linie
 * antworten gleichzeitig; ausgelesen wird dann keiner von beiden. Deshalb
 * steht hier nicht „60 Zähler", sondern je Zähler sein Platz.
 */
export function Mbus({ daten }: { daten: MsrMbus }) {
  const [werte, setWerte] = useState<FilterWerte>({ medium: '', asp: '' });

  const zaehler = daten.zaehler ?? [];

  const asps = useMemo(() => Array.from(new Set(
    zaehler.map((z) => z.asp).filter((a): a is string => !!a))).sort(), [zaehler]);

  const filter: FilterDefinition[] = useMemo(() => [
    {
      schluessel: 'medium', beschriftung: 'Medium', art: 'auswahl',
      optionen: (daten.jeMedium ?? []).map((m) => ({
        wert: m.medium, beschriftung: MEDIUM_TEXT[m.medium] ?? m.medium,
      })),
    },
    {
      schluessel: 'asp', beschriftung: 'Schaltschrank', art: 'auswahl',
      optionen: asps.map((a) => ({ wert: a, beschriftung: a })),
    },
  ], [daten.jeMedium, asps]);

  const gefiltert = zaehler
    .filter((z) => !werte.medium || z.medium === werte.medium)
    .filter((z) => !werte.asp || z.asp === werte.asp);

  if (daten.anbindung !== 'bus') {
    return (
      <ContentSection titel="M-Bus">
        <EmptyState
          titel={`Dieses Projekt zählt über „${daten.anbindung}" — kein M-Bus-Adressplan.`}
          erklaerung={'Ein Adressplan entsteht erst, wenn die Einstellung '
            + '`zaehler_anbindung` auf `bus` steht (Festlegung des Fachplaners vom '
            + '16.09.2026, Frage 64). Ein stehengebliebener Adressplan wäre eine Liste, '
            + 'nach der niemand verdrahten darf — deshalb ist hier nichts und nicht '
            + 'etwas Veraltetes.'}
        />
      </ContentSection>
    );
  }

  const segmentSpalten: Spalte<Segment>[] = [
    {
      schluessel: 'asp', titel: 'Schaltschrank (Pegelwandler)', immerSichtbar: true,
      wert: (s2) => s2.asp ?? null,
      inhalt: (s2) => (s2.asp ?? <em className="schwach">ohne Schrank</em>),
    },
    { schluessel: 'segment', titel: 'Segment', art: 'zahl', breite: '110px',
      wert: (s2) => s2.segment },
    { schluessel: 'teilnehmer', titel: 'Teilnehmer', art: 'zahl', breite: '130px',
      wert: (s2) => s2.teilnehmer },
    // TM-8c: das Segment gegen die Grenze wie im Blatt „Segmente“ der Excel-Mappe.
    {
      schluessel: 'grenze', titel: 'gegen die Grenze', art: 'status', breite: '170px',
      wert: (s2) => (s2.ueberschritten ? 'über der Grenze' : 'im Rahmen'),
      inhalt: (s2) => (s2.ueberschritten
        ? <StatusBadge zustand="fehler" text="über der Grenze" klein />
        : <StatusBadge zustand="gut" text="im Rahmen" klein />),
    },
  ];

  const zaehlerSpalten: Spalte<Zaehler>[] = [
    { schluessel: 'medium', titel: 'Medium', breite: '110px',
      wert: (z2) => MEDIUM_TEXT[z2.medium] ?? z2.medium },
    { schluessel: 'bezeichnung', titel: 'Zähler', immerSichtbar: true,
      wert: (z2) => z2.bezeichnung },
    { schluessel: 'messstelle', titel: 'Messstelle', wert: (z2) => z2.messstelle ?? null },
    {
      schluessel: 'asp', titel: 'Schaltschrank', wert: (z2) => z2.asp ?? null,
      inhalt: (z2) => (z2.asp ?? <em className="schwach">ohne</em>),
    },
    { schluessel: 'segment', titel: 'Segment', art: 'zahl', breite: '100px',
      wert: (z2) => z2.segment },
    {
      schluessel: 'primaeradresse', titel: 'Primäradresse', art: 'zahl', breite: '130px',
      titelHinweis: 'Wird am Zähler von Hand eingestellt (EN 13757-3, 1 bis 250 je Segment).',
      wert: (z2) => z2.primaeradresse,
      inhalt: (z2) => <strong>{z2.primaeradresse}</strong>,
    },
  ];

  const ueberschritten = (daten.segmente ?? []).filter((s2) => s2.ueberschritten).length;

  return (
    <>
      <KpiReihe>
        <KpiCard titel="Zähler" wert={zaehler.length} />
        <KpiCard
          titel="Segmente" wert={daten.segmente.length}
          vergleich={daten.grenzeJeSegment
            ? `Grenze ${formatiereZahl(daten.grenzeJeSegment)} Teilnehmer je Segment`
            : daten.teilnehmerJeSegment
              ? `${formatiereZahl(daten.teilnehmerJeSegment)} Teilnehmer je Segment`
              : undefined}
          {...(ueberschritten > 0
            ? { zustand: 'fehler' as const, zustandText: `${formatiereZahl(ueberschritten)} über der Grenze` }
            : {})}
          fussnote="Primäradresse 1 bis 250 je Segment (EN 13757-3)"
        />
        {(daten.jeMedium ?? []).map((m) => (
          <KpiCard key={m.medium} titel={MEDIUM_TEXT[m.medium] ?? m.medium} wert={m.zahl}
            einheit="Zähler" />
        ))}
      </KpiReihe>

      <ContentSection
        titel="Segmente"
        beschreibung="Je Pegelwandler ein Segment — zwei Zähler mit derselben Adresse auf
                      derselben Linie antworten gleichzeitig, ausgelesen wird dann keiner."
      >
        <DataTable
          name="Segmente"
          spalten={segmentSpalten}
          zeilen={daten.segmente ?? []}
          schluessel={(s2) => `${s2.asp ?? 'ohne'}-${s2.segment}`}
          dicht
          suche={false}
          spaltenwahl={false}
          leer={<EmptyState klein titel="Noch kein M-Bus-Segment gebildet." />}
        />
      </ContentSection>

      <ContentSection titel="Zähler, Segmente, Primäradressen">
        <FilterBar filter={filter} werte={werte} beiAenderung={setWerte} inLeiste={2} />

        <DataTable
          name="M-Bus-Zähler"
          spalten={zaehlerSpalten}
          zeilen={gefiltert}
          schluessel={(z2) => z2.id}
          dicht
          ausfuhr
          ausfuhrDateiname="msr-mbus-adressplan"
          leer={(
            <EmptyState
              titel={zaehler.length === 0
                ? 'Noch kein Zähler am M-Bus.'
                : 'Kein Zähler passt zu diesen Filtern.'}
              erklaerung={zaehler.length === 0
                ? 'Zähler entstehen aus dem Ableitungslauf über die Gewerkemodelle.'
                : `${zaehler.length} Zähler sind vorhanden.`}
              aktion={zaehler.length > 0 ? (
                <button type="button" className="m-knopf"
                  onClick={() => setWerte({ medium: '', asp: '' })}>
                  Filter zurücksetzen
                </button>
              ) : undefined}
            />
          )}
        />
      </ContentSection>
    </>
  );
}
